import { getValidatedLimitParam, getValidatedPageParam, useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useQueryWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { useIsLoggedIn } from 'hooks/use-is-logged-in';
import { TParam, usePageParams } from 'hooks/use-page-params';
import { UseQueryOptions, UseQueryResult } from 'react-query';

const endpoint = 'ums/v1/crm/brands/poc/calls';
export const getPocAgentCallsEndpointIdentifier = endpoint;

export interface IPocAgentCallData {
  id: number;
  brand: {
    id: number;
    name: string;
  };
  aiAgentProvider: string;
  callId: string;
  callType: string;
  agentId: string;
  callStatus: string;
  durationMs: number;
  transcriptObject: {
    role: 'user' | 'agent';
    content: string;
  }[];
  callCost: {
    combined_cost: number;
    product_costs: {
      cost: number;
      product: string;
      unit_price: number;
    }[];
    total_duration_seconds: number;
    total_duration_unit_price: number;
  };
  recordingUrl: string;
  disconnectionReason: string;
  callAnalysis: {
    call_summary: string;
    in_voicemail: boolean;
    user_sentiment: string;
    call_successful: boolean;
    custom_analysis_data: {
      contact_details: string;
    };
  };
  optOutSensitiveDataStorage: boolean;
  fromNumber: string;
  toNumber: string;
  ingestedAt: string;
  internalFeedback: string | null;
}

type TData = {
  pageCount: number;
  total: number;
  data: IPocAgentCallData[];
};

type TError = AxiosError;
type TQueryKey = [
  typeof endpoint,
  {
    page: TParam;
    limit: TParam;
    sort: TParam;
    brandId: TParam;
    status: TParam;
    sentiment: TParam;
    filter: TParam;
  }
];
type Options = Omit<UseQueryOptions<unknown, TError, TData, TQueryKey>, 'queryKey' | 'queryFn'>;
type ReturnType = UseQueryResult<TData, TError>;

export function useGetPocAgentCallsApi(options?: Options): ReturnType {
  const toast = useCustomToast();
  const page = getValidatedPageParam();
  const limit = getValidatedLimitParam();
  const isLoggedIn = useIsLoggedIn();
  const { getPageParams } = usePageParams();
  const { sort, brandId, status, sentiment, filter } = getPageParams();

  const queryFn = async () => {
    try {
      const { data } = await http.get(endpoint, {
        params: {
          page,
          limit,
          sort,
          brandId,
          status,
          sentiment,
          filter,
        },
      });

      return data;
    } catch (error: any) {
      if (!error.response.status || !error.response.data.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  return useQueryWrapper(
    [
      endpoint,
      {
        page,
        limit,
        sort,
        brandId,
        status,
        sentiment,
        filter,
      },
    ],
    queryFn,
    {
      enabled: isLoggedIn,
      ...options,
    }
  );
}
